/**
 * Turns one `buildArTableSheetSchema` value into the ExcelJS cell both XLSX
 * writers add: `exportArTableToExcel` and `createXlsxWorkbookBlob`.
 *
 * A value writes as its own JS type. OOXML has no column type — `<col>` carries
 * width and style only, the type is the per-cell `t` attribute — so `schemaItem.type`
 * governs width, the column `numFmt` and the `(CT)` label but never gates a write:
 * gating dropped every bundle-header roll-up reaching a Boolean or Date column
 * (SYD-2291). A per-cell `numFmt` comes back only when the value's display class
 * disagrees with the column's; `'General'` (builtin id 0) keeps a number in a date
 * column summable rather than a bogus date serial, and booleans need no override
 * because number formats do not apply to them. Anything that is neither blank, a
 * Date, a number, a boolean nor linkable text is blank: ExcelJS JSON-stringifies an
 * unrecognised object straight into the cell as literal text.
 */

import type { CellValue } from 'exceljs';
import { exceljsStringCellValueWithOptionalLastLineHyperlink } from './exceljsStringCellValueWithOptionalLastLineHyperlink';
import type { ArTableSheetSchemaItem, ArTableSheetValue } from './buildArTableSheetSchema';

type ArTableSheetCell = { value: CellValue; numFmt?: string };

const DATE_NUMBER_FORMAT = 'MM/DD/YYYY HH:mm:ss';
const GENERAL_NUMBER_FORMAT = 'General';

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value !== '';

const coerceArTableSheetCell = (
  schemaItem: Pick<ArTableSheetSchemaItem, 'type'>,
  raw: ArTableSheetValue,
): ArTableSheetCell => {
  if (raw === null || raw === undefined || raw === '') return { value: null };
  if (raw instanceof Date) {
    if (schemaItem.type === Date) return { value: raw };
    return { value: raw, numFmt: DATE_NUMBER_FORMAT };
  }
  if (typeof raw === 'number') {
    if (schemaItem.type === Date) return { value: raw, numFmt: GENERAL_NUMBER_FORMAT };
    return { value: raw };
  }
  if (typeof raw === 'boolean') return { value: raw };
  if (typeof raw === 'string' || (isNonEmptyString(raw.display) && isNonEmptyString(raw.url))) {
    return { value: exceljsStringCellValueWithOptionalLastLineHyperlink(raw) };
  }
  return { value: null };
};

export { coerceArTableSheetCell };
